import { createSelector } from 'reselect';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { differenceInDays } from 'date-fns';
import {
  createUser,
  deleteRadarUser,
  fetchUserData,
  syncUserData as syncUserDataApi,
  updateUserAvatar,
  updateUserData,
  uploadUserAvatar,
} from '../api/radarProfile';
import { createReducer } from '../reducers/factory';
import { userSelector } from '../selectors';
import { radarConfigPlainSelector } from './radarConfig';

const name = 'radarUserProfile';
const nameForFactory = 'RADAR_USER_PROFILE';

const FETCH_SUCCESSFUL = `FETCH_${nameForFactory}_SUCCESSFUL`;
const FETCH_FAILED = `FETCH_${nameForFactory}_FAILED`;
export const CLEAR_RADAR_USER_DATA = 'CLEAR_RADAR_USER_DATA';

const handleSuccessfulFetch = (state, action) => ({
  ...state,
  fetchingInProgressSince: null,
  lastSuccessfulFetch: action.timestamp,
  fetchPromise: null,
  userData: action.userData,
  didInvalidate: false,
});

const initialState = {
  userData: {},
  guidelinesConsent: false,
};

const fetchReducer = createReducer(nameForFactory, {
  fetchTimeout: 30 * 1000,
  extendedReducer: {
    [FETCH_SUCCESSFUL]: handleSuccessfulFetch,
    [FETCH_FAILED]: (state, action) => {
      if (action?.reason?.status === 404) {
        return handleSuccessfulFetch(state, { ...action, userData: {} });
      }
      if (action?.reason?.status === 403) {
        return handleSuccessfulFetch(state, {
          ...action,
          userData: { userBannedAndDeleted: true },
        });
      }
      return {
        ...state,
        fetchingInProgressSince: null,
        lastFailedFetch: action.timestamp,
        fetchPromise: null,
      };
    },
  },
  invalidateActions: ['LOGIN_SUCCESSFUL', 'SET_LANGUAGE'],
  resetActions: ['LOGOUT_SUCCESSFUL'],
  minUpdate: 30 * 60 * 1000,
  initialState,
});

fetchReducer.fetchSuccessful = (userData) => ({
  type: FETCH_SUCCESSFUL,
  userData,
  timestamp: Date.now(),
});

export const radarUserDataPlainSelector = ({ [name]: radarUserProfile }) =>
  radarUserProfile;

export const fetchRadarUserProfileAction = () => async (dispatch, getState) => {
  const {
    auth: { token },
  } = getState();

  const fetchPromise = (async () => {
    try {
      dispatch(fetchReducer.fetchSuccessful(await fetchUserData(token)));
    } catch (e) {
      dispatch(fetchReducer.fetchFailed(e));
      throw e;
    }
  })();

  dispatch(fetchReducer.fetchInProgress(fetchPromise));
  return fetchPromise;
};

export const radarUserDataSelector = createSelector(
  radarUserDataPlainSelector,
  ({
    fetchingInProgressSince,
    lastSuccessfulFetch,
    lastFailedFetch,
    ...rest
  }) => ({
    ...rest,
    loading:
      !!fetchingInProgressSince || (!lastSuccessfulFetch && !lastFailedFetch),
  }),
);

export const friendCodeSelector = ({ [name]: { userData } }) => userData?.uid;
export const guidelinesConsentSelector = ({ [name]: { guidelinesConsent } }) =>
  guidelinesConsent;

export const userAvatarSelector = createSelector(
  radarUserDataPlainSelector,
  radarConfigPlainSelector,
  ({ userData }, { config }) =>
    config.avatars?.find(({ id }) => id === userData.avatar),
);

export const updateUserRadarAvatar = (avatar) => async (dispatch, getState) => {
  const {
    auth: { token },
  } = getState();

  dispatch({
    type: 'UPDATE_USER',
    newUserData: await updateUserAvatar(token, avatar),
  });
};

export const clearUserData = () => ({
  type: CLEAR_RADAR_USER_DATA,
});

export const deleteRadarProfile = () => async (dispatch, getState) => {
  const {
    auth: { token },
  } = getState();
  return deleteRadarUser(token)
    .then(() => {
      dispatch(clearUserData());
    })
    .catch((e) => Promise.reject(e));
};

export const uploadUserRadarAvatar = (avatar) => async (dispatch, getState) => {
  const {
    auth: { token },
  } = getState();

  dispatch({
    type: 'UPDATE_USER',
    newUserData: await uploadUserAvatar(token, avatar),
  });
};

export const updateUserRadarData =
  (newUserData) => async (dispatch, getState) => {
    const {
      auth: { token },
    } = getState();

    dispatch({
      type: 'UPDATE_USER',
      newUserData: await updateUserData(token, newUserData),
    });
  };

export const syncUserData = () => async (dispatch, getState) => {
  const {
    auth: { token },
  } = getState();

  dispatch({
    type: 'UPDATE_USER',
    newUserData: await syncUserDataApi(token),
  });
};

export const increaseSwipesCount = () => ({
  type: 'UPDATE_SWIPES_COUNT',
  value: 1,
});

export const decreaseSwipesCount = () => ({
  type: 'UPDATE_SWIPES_COUNT',
  value: -1,
});

export const setSwipesCount = (value) => ({
  type: 'SET_SWIPES_COUNT',
  value,
});

export const setGuidelinesConsent = (consent) => ({
  type: 'SET_GUIDELINES_CONSENT',
  consent,
});

export const updateSuperlikeBalance = (
  superLikeBalance,
  dailySuperLikeUsedAt,
) => ({
  type: 'UPDATE_SUPERLIKE_USER_BALANCE',
  superLikeBalance,
  dailySuperLikeUsedAt,
});

const shouldSyncUserData = (radarUser, lootboyUser) => {
  const { birthYear, country, gender, nickname, id, subscriptionValidUntil } =
    radarUser;
  const {
    birthday,
    country: lCountry,
    gender: genderText,
    username: lNickname,
    subscriptionValidUntil: subscriptionValidUntilDate,
  } = lootboyUser;

  const lBirthYear = birthday && new Date(birthday)?.getFullYear();
  const lGender = (genderText && genderText?.toUpperCase().charAt(0)) || null;
  const lSubscriptionValidUntil = new Date(
    subscriptionValidUntilDate || 0,
  ).getTime();

  const didDataChange =
    (lBirthYear && lBirthYear !== birthYear) ||
    (lCountry && lCountry !== country) ||
    lGender !== gender ||
    (lNickname && lNickname !== nickname) ||
    lSubscriptionValidUntil !== subscriptionValidUntil;

  return !!id && didDataChange;
};

export const fetchRadarUserDataIfNeeded = () => async (dispatch, getState) => {
  const { fetchPromise, userData, ...state } = radarUserDataPlainSelector(
    getState(),
  );
  const {
    auth: {
      token,
      user: { data: lootboyUserData },
    },
  } = getState();

  if (!token) {
    return Promise.resolve();
  }

  try {
    const shouldSync = shouldSyncUserData(userData, lootboyUserData);
    if (shouldSync) {
      await dispatch(syncUserData());
    }
  } catch {
    // ignore errors
  }

  if (fetchReducer.shouldFetch(state)) {
    return dispatch(fetchRadarUserProfileAction());
  }

  return typeof fetchPromise?.then === 'function' ? fetchPromise : userData;
};

export const useRadarProfileData = () => {
  const data = useSelector(radarUserDataSelector);
  const dispatch = useDispatch();
  const { didInvalidate } = data;

  useEffect(() => {
    dispatch(fetchRadarUserDataIfNeeded()).catch(() => {});
  }, [dispatch, didInvalidate]);

  return data;
};

export const useAccountAge = () => {
  const { createdAt } = useSelector(userSelector);
  return useMemo(
    () => createdAt && differenceInDays(new Date(createdAt), new Date()),
    [createdAt],
  );
};

export const createRadarUser = (userData) => async (dispatch, getState) => {
  const {
    auth: { token },
  } = getState();

  if (!token) {
    return Promise.resolve();
  }

  return createUser(token, userData)
    .then((resultUserData) => {
      dispatch(fetchReducer.fetchSuccessful(resultUserData));
      return Promise.resolve(resultUserData);
    })
    .catch((e) => Promise.reject(e));
};

function RadarUserReducer(state = fetchReducer.INITIAL_STATE, action = {}) {
  switch (action.type) {
    case 'UPDATE_USER':
      return {
        ...state,
        userData: { ...state.userData, ...action.newUserData },
      };
    case 'SET_GUIDELINES_CONSENT':
      return {
        ...state,
        guidelinesConsent: action.consent,
      };
    case 'UPDATE_SUPERLIKE_USER_BALANCE':
      return {
        ...state,
        userData: {
          ...state.userData,
          superLikeBalance: action.superLikeBalance,
          dailySuperLikeUsedAt: action.dailySuperLikeUsedAt,
        },
      };
    case 'UPDATE_SWIPES_COUNT':
      return {
        ...state,
        userData: {
          ...state.userData,
          swipesCount: (state.userData?.swipesCount || 0) + action.value,
        },
      };
    case 'SET_SWIPES_VALUE':
      return {
        ...state,
        userData: {
          ...state.userData,
          swipesCount: action.value,
        },
      };
    case CLEAR_RADAR_USER_DATA:
      return {
        ...state,
        ...initialState,
      };
    default:
      return fetchReducer(state, action);
  }
}

export default { [name]: RadarUserReducer };
